/* istanbul ignore file */
import React from 'react';
import { useParams } from 'react-router-dom';
import { uniqBy } from 'lodash';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/cssxp-client-utils';
import {
  BillingStatus,
  FetchInvoiceGroupDetailsForSubscriptionTablePpcQuery,
  FetchInvoiceGroupDetailsForSubscriptionTablePpcQueryVariables,
  LinkedDestinationEntitlement,
  TransactionAccountProduct,
} from '@css-xp/graphtypes/src/generated-types-new';
import { OrderItemType } from '@atlassian/cssxp-types-entitlement';
import {
  CCP_SEARCH_MAX_LIMIT,
  defaultBillingProfileSortOptions,
  maxThresholdForResults,
  SUBSCRIPTION_CONSTANTS,
} from '@css-xp/ppc-constants';
import { NotificationTypes, errorNotificationBuilder, useNotification } from '@css-xp/base';
import { failureMessage } from '@css-xp/ppc-common';
import { updateEntitlementPlanV2 } from '@atlassian/cssxp-proxies';
import { fetchInvoiceGroupDetailsForSubscriptionTablePPCQuery } from '../queries/fetch-invoice-group-details-for-subscription-table-ppc';
import { fetchShipToIdsByCompanyNameQuery } from '../queries/fetch-ship-to-ids-by-company-name';
import { fetchBillingProfilesForBulkTransferQuery } from '../queries/fetch-billing-profiles-for-bulk-transfer';
import { fetchProductsAndLinkedEntitlements } from '../queries/fetch-products-and-linked-entitlements';

type ProductAndAppSubscriptionWithLinkedEntitlements = TransactionAccountProduct & {
  linkedEntitlements: LinkedDestinationEntitlement[];
};

type ProductAndAppSubscriptionsWithLinkedEntitlements = ProductAndAppSubscriptionWithLinkedEntitlements[];

export const useBulkTransferSubscriptions = (
  isSandboxSupportForSubscriptionsEnabled?: boolean,
  isFetchBillingProfilesViaCCPEnabled?: boolean,
  isSandboxDecouplingEnabled?: boolean
) => {
  const ansClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const { setNotificationSent, sendErrorNotification, notificationSent } = useNotification();

  const { txaId: transactionAccountId, billingProfileId } = useParams<{ txaId: string; billingProfileId: string }>();

  const [areBillingProfilesBeingFetched, setAreBillingProfilesBeingFetched] = React.useState(false);

  const commonFilterOptions = [
    {
      key: 'not',
      value: JSON.stringify([
        SUBSCRIPTION_CONSTANTS.appsFilter,
        ...(isSandboxSupportForSubscriptionsEnabled ? [] : [SUBSCRIPTION_CONSTANTS.sandboxFilter]),
      ]),
    },
    { key: 'status', value: 'Active' },
    {
      key: 'invoiceGroupId',
      value: billingProfileId,
    },
  ];

  const getTheAlternateBillingProfilesForTheCurrentCompany = async () => {
    setNotificationSent(false);
    setAreBillingProfilesBeingFetched(true);
    try {
      const { data, error } = await ansClient.query<
        FetchInvoiceGroupDetailsForSubscriptionTablePpcQuery,
        FetchInvoiceGroupDetailsForSubscriptionTablePpcQueryVariables
      >({
        query: fetchInvoiceGroupDetailsForSubscriptionTablePPCQuery,
        variables: {
          id: billingProfileId,
          txaId: transactionAccountId,
        },
      });
      if (error) {
        throw error;
      }
      const companyName = data?.invoiceGroup?.shipToParty?.name;
      const { data: shipToIdsData, error: fetchShipToIdsByCompanyNameError } = await ansClient.query({
        query: fetchShipToIdsByCompanyNameQuery,
        variables: {
          searchParams: {
            companyName,
            transactionAccountId,
          },
          pageParams: {
            size: 100,
          },
        },
      });
      if (fetchShipToIdsByCompanyNameError) {
        throw fetchShipToIdsByCompanyNameError;
      }
      const shipToPartyIds = shipToIdsData?.shipToParty?.nodes?.map((node: { id: string }) => node.id) ?? [];

      const { data: allBillingProfilesData, error: fetchBillingProfilesForBulkTransferError } = await ansClient.query({
        query: fetchBillingProfilesForBulkTransferQuery,
        variables: {
          id: transactionAccountId,
          filterOptions: [
            {
              key: 'or',
              // sending multiple ship-to ids
              value: JSON.stringify(shipToPartyIds.map((shipToId: string) => ({ shipToId }))),
            },
          ],
          searchOptions: {
            first: isFetchBillingProfilesViaCCPEnabled ? CCP_SEARCH_MAX_LIMIT : maxThresholdForResults,
            offset: 0,
            sort: defaultBillingProfileSortOptions,
          },
          isFetchBillingProfilesViaCCPEnabled,
        },
      });
      if (fetchBillingProfilesForBulkTransferError) {
        throw fetchBillingProfilesForBulkTransferError;
      }

      const availableBillingProfilesForCustomer =
        (isFetchBillingProfilesViaCCPEnabled
          ? allBillingProfilesData?.transactionAccount?.billingProfilesFromCCP?.nodes
          : allBillingProfilesData?.transactionAccount?.billingProfiles?.nodes) ?? [];

      const alternateBillingProfilesForCustomer = availableBillingProfilesForCustomer.filter(
        (billingProfile: any) => billingProfile && billingProfile.id !== billingProfileId
      );
      const currentBillingProfileForCustomer = availableBillingProfilesForCustomer.find(
        (billingProfile: any) => billingProfile && billingProfile.id === billingProfileId
      );
      return {
        alternateBillingProfilesForCustomer,
        currentBillingProfileForCustomer,
        companyName,
      };
    } catch (err) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('We are unable to fetch available billing profiles for the current company.')
          .withMessage(failureMessage())
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
      return {};
    } finally {
      setAreBillingProfilesBeingFetched(false);
    }
  };

  const prepareEntitlementListsWithOrderType = (subscriptions: ProductAndAppSubscriptionsWithLinkedEntitlements) => {
    const entitlementsList = subscriptions.map((subscription) => {
      const actualSubscription = {
        entitlementId: subscription.id,
        productId: subscription.productId,
        offeringId: subscription.offeringId,
        orderType: OrderItemType.ACCOUNT_MODIFICATION_ORDER,
      };
      const isSandboxEntitlement = !!subscription.isSandbox;
      const activeLinkedEntitlements = subscription.linkedEntitlements
        .filter(
          (linkedEntitlement) =>
            linkedEntitlement.billingStatus === BillingStatus.ACTIVE &&
            // for sandbox entitlement, only pass the linked sandbox container and sandbox apps
            // for prod entitlement, only pass the linked container and apps
            (isSandboxDecouplingEnabled ? !!linkedEntitlement.isSandbox === isSandboxEntitlement : true)
        )
        .map((linkedEntitlement) => {
          return {
            entitlementId: linkedEntitlement.entitlementId,
            productId: linkedEntitlement.productId,
            offeringId: linkedEntitlement.offeringId,
            orderType: OrderItemType.ACCOUNT_MODIFICATION_ORDER,
          };
        });
      return [actualSubscription, ...activeLinkedEntitlements];
    });
    const uniqueEntitlements = uniqBy(entitlementsList.flat(), 'entitlementId');
    return uniqueEntitlements;
  };

  const transferSubscriptions = async ({
    selectedSubscriptionIds,
    areAllRowsSelected = false,
    selectedBillingProfileId,
  }: {
    selectedSubscriptionIds: string[];
    selectedBillingProfileId: string | undefined;
    areAllRowsSelected?: boolean;
  }) => {
    let variables = {
      id: transactionAccountId,
      searchOptions: {
        first: maxThresholdForResults,
        offset: 0,
      },
      filterOptions: [...commonFilterOptions],
    };

    if (!areAllRowsSelected) {
      variables = {
        ...variables,
        filterOptions: [
          ...variables.filterOptions,
          {
            key: 'idIsIn',
            // @ts-ignore
            values: selectedSubscriptionIds,
          },
        ],
      };
    }
    // We are fetching all the linked apps, sandbox products, container etc. in this query
    const { data, error: fetchProductsAndLinkedEntitlementsError } = await ansClient.query({
      query: fetchProductsAndLinkedEntitlements,
      variables,
    });

    if (fetchProductsAndLinkedEntitlementsError) {
      // @ts-ignore
      throw new Error(fetchProductsAndLinkedEntitlementsError.message, {
        cause: fetchProductsAndLinkedEntitlementsError,
      });
    }

    const subscriptions = data?.transactionAccount?.productSubscriptionsInPPC?.nodes ?? [];
    const listOfEntitlementsForOms = prepareEntitlementListsWithOrderType(subscriptions);

    // Placing the order
    const response = await updateEntitlementPlanV2({
      // @ts-ignore
      entitlements: listOfEntitlementsForOms,
      invoiceGroupId: selectedBillingProfileId,
      transactionAccountId,
    });
    if (!response.orderId) {
      // @ts-ignore
      throw new Error(response.message, {
        cause: response,
      });
    }
    return response;
  };

  return {
    transferSubscriptions,
    getTheAlternateBillingProfilesForTheCurrentCompany,
    areBillingProfilesBeingFetched,
  };
};
